import {Timer} from '@src/components/Timer'
import {Button, ButtonGroup} from '@therms/atalaya'
import {
  FormikDateTimePicker,
  LocationName,
  RPCDocs,
} from '@therms/react-modules'
import classNames from 'classnames'
import dayjs from 'dayjs'
import {Formik, type FormikHelpers} from 'formik'
import * as Yup from 'yup'
import React from 'react'
import Status = RPCDocs.Patrol.GetPatrolRouteRunDetails.v1.LocationVisitsStatus
import NewStatus = RPCDocs.Patrol.UpdatePatrolRouteRunVisit.v1.NewStatus

const ValidationSchema = Yup.object().shape({
  arrivalTime: Yup.string(),
  departureTime: Yup.string(),
  status: Yup.string(),
})

type FormValues = {
  arrivalTime?: string
  departureTime?: string
  status?: string
}

const initialValues = {
  arrivalTime: undefined,
  departureTime: undefined,
  status: undefined,
}

interface InProgressVisitRunListItemProps {
  arrivalTime?: string
  departureTime?: string
  lengthMin?: number
  locationId: string
  onSubmit: (
    args: RPCDocs.Patrol.UpdatePatrolRouteRunVisit.v1.Args,
  ) => Promise<void>
  runVisitId: string
  status?: Status
  visitNotes?: string
  visitRunNotes?: string
}

function InProgressVisitRunListItem({
  arrivalTime,
  departureTime,
  lengthMin,
  locationId,
  onSubmit,
  runVisitId,
  status,
  visitNotes,
  visitRunNotes,
}: InProgressVisitRunListItemProps) {
  const handleSubmit = async (
    values: FormValues,
    actions: FormikHelpers<FormValues>,
  ) => {
    actions.setSubmitting(true)

    const args: RPCDocs.Patrol.UpdatePatrolRouteRunVisit.v1.Args = {
      arrivalTime: values.arrivalTime,
      departureTime: values.departureTime,
      id: runVisitId,
    }

    if (values.status) args.newStatus = values.status as NewStatus

    await onSubmit(args)

    actions.setFieldValue('status', undefined)
  }

  return (
    <div>
      <div className="flex">
        <span className="text-md text-color-strong grow font-medium tracking-wide">
          <LocationName locationId={locationId} />
        </span>

        <div className="gap-x-xs flex flex-wrap-reverse items-baseline">
          <div className="">
            <span className="text-color-strong">{lengthMin || 0}</span>
            <span> Minutes</span>
            <span className="text-color-subtle text-xs"> Minimum</span>
          </div>

          {!!arrivalTime && (
            <Timer
              className="text-color-strong px-xxs bg-info-faded rounded font-mono text-sm"
              timestampStart={arrivalTime}
              timestampEnd={departureTime}
            />
          )}
        </div>
      </div>

      {(visitRunNotes || visitNotes) && (
        <div className="mt-base space-y-xs">
          {visitRunNotes && (
            <div className="text-sm leading-relaxed">
              <span className="text-color-strong font-bold">Run Notes</span>{' '}
              <span>{visitRunNotes}</span>
            </div>
          )}

          {visitNotes && (
            <div className="text-sm leading-relaxed">
              <span className="font-bold">Notes</span>{' '}
              <span className="text-color-subtle">{visitNotes}</span>
            </div>
          )}
        </div>
      )}

      <Formik<FormValues>
        initialValues={{
          arrivalTime: arrivalTime || initialValues.arrivalTime,
          departureTime: departureTime || initialValues.departureTime,
        }}
        onSubmit={handleSubmit}
        validationSchema={ValidationSchema}
      >
        {({isSubmitting, setFieldValue, submitForm, values}) => {
          const visitEndingPrematurely =
            !lengthMin ||
            dayjs(values.departureTime || undefined).diff(
              values.arrivalTime || undefined,
              'minutes',
            ) < lengthMin

          return (
            <div
              className={classNames(
                {'opacity-50': isSubmitting},
                'gap-xs mt-sm flex flex-wrap items-end',
              )}
            >
              <div className="grow">
                <FormikDateTimePicker
                  disabled={isSubmitting}
                  label="Arrival"
                  mode="time"
                  name="arrivalTime"
                  noInitialValue
                />
              </div>

              <div className="grow">
                <FormikDateTimePicker
                  disabled={isSubmitting}
                  label="Departure"
                  mode="time"
                  name="departureTime"
                  noInitialValue
                />
              </div>

              <ButtonGroup disabled={isSubmitting} className="mb-px ml-auto">
                <Button
                  active={status === Status.Skipped}
                  onClick={() => {
                    setFieldValue('status', Status.Skipped)
                    submitForm()
                  }}
                >
                  Skip
                </Button>
                <Button
                  active={status === Status.Completed}
                  onClick={() => {
                    setFieldValue('status', Status.Completed)
                    submitForm()
                  }}
                  variant={visitEndingPrematurely ? 'caution' : 'positive'}
                >
                  Complete
                </Button>
              </ButtonGroup>
            </div>
          )
        }}
      </Formik>
    </div>
  )
}

export {InProgressVisitRunListItem}
